import React, { useState, useEffect, useRef } from 'react';
import { AppAspect, AppTheme, AppRelationship, ID, SubTheme, AppCitation } from './types';
import { cn } from './lib/utils';
import Portal from './Portal';

// Citation popover component
const CitationPopover: React.FC<{
    citations: AppCitation[];
    globalFeedbackIdToNumberMap: Map<string, number>;
    isOpen: boolean;
    onClose: () => void;
    className?: string;
}> = ({ citations, globalFeedbackIdToNumberMap, isOpen, onClose, className }) => {
    if (!isOpen) return null;

    return (
        <div
            className={cn(
                "absolute z-50 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 p-2 min-w-[90px]",
                "isolate", // Isolate from parent backdrop effects
                "backdrop-blur-none", // Ensure no blur is applied
                className
            )}
            onClick={(e) => e.stopPropagation()}
            style={{
                transform: 'translateZ(0)', // Force GPU acceleration
                willChange: 'transform' // Optimize for transforms
            }}
        >
            <div className="text-[10px] font-medium text-gray-600 dark:text-gray-300 mb-1.5">Citations</div>
            <div className="flex flex-col gap-0.5">
                {citations.map((citation, index) => {
                    const globalCitationNumber = globalFeedbackIdToNumberMap.get(citation.feedback_id);
                    return (
                        citation.url && globalCitationNumber !== undefined && (
                            <a
                                key={`citation-${citation.feedback_id}-${index}`}
                                href={citation.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline block"
                                onClick={(e) => e.stopPropagation()}
                            >
                                [{globalCitationNumber}]
                            </a>
                        )
                    );
                })}
            </div>
        </div>
    );
};

// Citation badge component
const CitationBadge: React.FC<{
    count: number;
    citations: AppCitation[];
    globalFeedbackIdToNumberMap: Map<string, number>;
    color: string;
}> = ({ count, citations, globalFeedbackIdToNumberMap, color }) => {
    const [showPopover, setShowPopover] = useState(false);
    const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Calculate popover position when opening
    useEffect(() => {
        if (showPopover && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPopoverPosition({
                top: rect.bottom + 2, // 2px gap
                left: rect.right - 90 // Align to right edge, popover is min 90px wide
            });
        }
    }, [showPopover]);

    // Close popover when clicking outside
    useEffect(() => {
        if (showPopover) {
            const handleClickOutside = (e: MouseEvent) => {
                // Check if click is outside the citation badge
                const target = e.target as HTMLElement;
                if (!target.closest('[data-citation-badge]') && !target.closest('[data-citation-popover]')) {
                    setShowPopover(false);
                }
            };

            // Add slight delay to prevent immediate closing
            setTimeout(() => {
                document.addEventListener('click', handleClickOutside);
            }, 0);

            return () => {
                document.removeEventListener('click', handleClickOutside);
            };
        }
    }, [showPopover]);

    return (
        <div className="relative" data-citation-badge>
            <button
                ref={buttonRef}
                onClick={(e) => {
                    e.stopPropagation();
                    setShowPopover(!showPopover);
                }}
                className={cn(
                    "p-0.5 rounded hover:opacity-80 transition-all",
                    color
                )}
                title={`${count} citation${count > 1 ? 's' : ''}`}
            >
                {/* Link icon instead of count */}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-2.5 w-2.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                </svg>
            </button>

            {showPopover && (
                <Portal>
                    <div
                        data-citation-popover
                        className="fixed z-50 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 p-2 min-w-[90px]"
                        style={{
                            top: `${popoverPosition.top}px`,
                            left: `${popoverPosition.left}px`,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="text-[10px] font-medium text-gray-600 dark:text-gray-300 mb-1.5">Citations</div>
                        <div className="flex flex-col gap-0.5">
                            {citations.map((citation, index) => {
                                const globalCitationNumber = globalFeedbackIdToNumberMap.get(citation.feedback_id);
                                return (
                                    citation.url && globalCitationNumber !== undefined && (
                                        <a
                                            key={`citation-${citation.feedback_id}-${index}`}
                                            href={citation.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[10px] text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline block"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            [{globalCitationNumber}]
                                        </a>
                                    )
                                );
                            })}
                        </div>
                    </div>
                </Portal>
            )}
        </div>
    );
};

// Theme card with minimal Linear-like styling
const ThemeCard: React.FC<{
    theme: AppTheme;
    isSelected: boolean;
    onSelect: (themeName: ID) => void;
    aspectLevel: number;
    globalFeedbackIdToNumberMap: Map<string, number>;
}> = ({ theme, isSelected, onSelect, aspectLevel, globalFeedbackIdToNumberMap }) => {
    const hasCitations = theme.citations && theme.citations.length > 0;
    const hasSubThemes = theme.sub_themes && theme.sub_themes.length > 0;

    // Start collapsed by default for all levels
    const [isExpanded, setIsExpanded] = useState(false);

    // Level-based minimal color accents - adjusted for dark mode
    const levelStyles = {
        1: {
            iconColor: "text-purple-500 dark:text-purple-400",
            textColor: "text-purple-600 dark:text-purple-400",
            hoverBg: "hover:bg-purple-50/30 dark:hover:bg-purple-900/20",
            selectedBg: "bg-purple-100/40 dark:bg-purple-900/30",
            selectedTextColor: "text-purple-700 dark:text-purple-300",
            citationBg: "text-purple-500 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30"
        },
        2: {
            iconColor: "text-violet-500 dark:text-violet-400",
            textColor: "text-violet-600 dark:text-violet-400",
            hoverBg: "hover:bg-violet-50/30 dark:hover:bg-violet-900/20",
            selectedBg: "bg-violet-100/40 dark:bg-violet-900/30",
            selectedTextColor: "text-violet-700 dark:text-violet-300",
            citationBg: "text-violet-500 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/30"
        },
        3: {
            iconColor: "text-cyan-500 dark:text-cyan-400",
            textColor: "text-cyan-600 dark:text-cyan-400",
            hoverBg: "hover:bg-cyan-50/30 dark:hover:bg-cyan-900/20",
            selectedBg: "bg-cyan-100/40 dark:bg-cyan-900/30",
            selectedTextColor: "text-cyan-700 dark:text-cyan-300",
            citationBg: "text-cyan-500 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/30"
        }
    };

    const styles = levelStyles[aspectLevel as keyof typeof levelStyles] || levelStyles[1];

    const handleThemeClick = () => {
        onSelect(theme.name);
    };

    const SubThemeItem: React.FC<{ subTheme: SubTheme }> = ({ subTheme }) => {
        const subThemeHasCitations = subTheme.citations && subTheme.citations.length > 0;

        return (
            <div className="pl-4 pr-1 py-0.5 relative group border-l border-purple-200/50 dark:border-purple-700/50 ml-2">
                {/* Enhanced connection line */}
                <div className="absolute -left-[1px] top-[0.5rem] w-2.5 h-[1px] bg-purple-200/50 dark:bg-purple-700/50" />

                {/* Smaller bullet point indicator */}
                <div className="absolute -left-[4px] top-[0.375rem] w-1.5 h-1.5 rounded-full bg-white/80 dark:bg-gray-800/80 border border-purple-300/50 dark:border-purple-600/50" />

                <div className="flex items-start gap-1">
                    <div className="flex-1">
                        <div className="flex items-center gap-1">
                            <span className="text-[10px] text-gray-600 dark:text-gray-300 leading-relaxed italic">{subTheme.name}</span>

                            {/* Sub-theme citation badge - even smaller */}
                            {subThemeHasCitations && subTheme.citations && (
                                <CitationBadge
                                    count={subTheme.citations.length}
                                    citations={subTheme.citations}
                                    globalFeedbackIdToNumberMap={globalFeedbackIdToNumberMap}
                                    color={cn("text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800")}
                                />
                            )}
                        </div>

                        {subTheme.description && (
                            <p className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed italic">
                                {subTheme.description}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div
            className={cn(
                "group",
                "mb-0.5",
                "relative"
            )}
        >
            {/* Subtle pulse animation for selected themes */}
            {isSelected && (
                <div className="absolute inset-0 rounded-md bg-purple-400/20 dark:bg-purple-500/20 animate-pulse" />
            )}

            {/* Main theme content */}
            <div
                className={cn(
                    "px-3 py-2 rounded-md transition-all duration-200",
                    "backdrop-blur-sm",
                    "border border-transparent",
                    isSelected
                        ? cn(styles.selectedBg, "border-purple-300/30 dark:border-purple-600/30", "shadow-sm")
                        : cn("hover:bg-white/30 dark:hover:bg-gray-800/30", styles.hoverBg, "hover:border-purple-200/20 dark:hover:border-purple-700/20"),
                    "relative z-10"
                )}
            >
                <div className="flex items-center gap-2">
                    {/* Expand/collapse chevron for themes with sub-themes */}
                    {hasSubThemes ? (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsExpanded(!isExpanded);
                            }}
                            className="p-0.5 rounded hover:bg-purple-200/30 dark:hover:bg-purple-800/30 transition-colors"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={cn(
                                    "h-2.5 w-2.5 transition-transform duration-200 text-purple-400 dark:text-purple-500",
                                    isExpanded ? "transform rotate-90" : ""
                                )}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                />
                            </svg>
                        </button>
                    ) : (
                        <div className="w-3" /> // Spacer for alignment
                    )}

                    {/* Feedback/comment icon instead of diamond for themes */}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={cn(
                            "h-3 w-3",
                            "cursor-pointer",
                            isSelected ? styles.iconColor : "text-purple-400 dark:text-purple-500"
                        )}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        onClick={handleThemeClick}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                        />
                    </svg>

                    {/* Theme name - italicized for feedback feel */}
                    <span
                        className={cn(
                            "text-xs flex-1 italic font-light",
                            hasSubThemes ? "cursor-pointer" : "",
                            isSelected ? styles.selectedTextColor : "text-gray-700 dark:text-gray-200"
                        )}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (hasSubThemes) {
                                setIsExpanded(!isExpanded);
                            } else {
                                handleThemeClick();
                            }
                        }}
                    >
                        {theme.name}
                    </span>

                    {/* Citation badge */}
                    {hasCitations && theme.citations && (
                        <CitationBadge
                            count={theme.citations.length}
                            citations={theme.citations}
                            globalFeedbackIdToNumberMap={globalFeedbackIdToNumberMap}
                            color={styles.citationBg}
                        />
                    )}
                </div>

                {/* Description */}
                {theme.description && (
                    <p className={cn(
                        "text-[10px] mt-1 ml-7 leading-relaxed italic",
                        isSelected ? "text-gray-600 dark:text-gray-300" : "text-gray-500 dark:text-gray-400"
                    )}>
                        {theme.description}
                    </p>
                )}
            </div>

            {/* Expanded content - only sub-themes now */}
            {isExpanded && hasSubThemes && (
                <div className="mt-0.5 mb-0.5 ml-8 bg-purple-50/20 dark:bg-purple-900/10 backdrop-blur-sm rounded py-1 border border-purple-200/20 dark:border-purple-700/20">
                    {theme.sub_themes.map((subTheme, index) => (
                        <SubThemeItem
                            key={`${theme.name}-sub-${index}`}
                            subTheme={subTheme}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// Aspect card with minimal Linear-like styling
const FinderAspectCard: React.FC<{
    aspect: AppAspect;
    isSelected: boolean;
    isFocused?: boolean;
    onSelect: () => void;
    hasChildren?: boolean;
    level: number;
}> = ({ aspect, isSelected, isFocused, onSelect, hasChildren, level }) => {
    const [showDescription, setShowDescription] = useState(false);

    // Level-based minimal color accents - adjusted for dark mode
    const levelStyles = {
        1: {
            iconColor: "text-blue-500 dark:text-blue-400",
            textColor: "text-gray-700 dark:text-gray-200",
            selectedTextColor: "text-blue-700 dark:text-blue-300",
            selectedBg: "bg-blue-50/40 dark:bg-blue-900/20",
            borderColor: "border-l-blue-500 dark:border-l-blue-400"
        },
        2: {
            iconColor: "text-indigo-500 dark:text-indigo-400",
            textColor: "text-gray-700 dark:text-gray-200",
            selectedTextColor: "text-indigo-700 dark:text-indigo-300",
            selectedBg: "bg-indigo-50/40 dark:bg-indigo-900/20",
            borderColor: "border-l-indigo-500 dark:border-l-indigo-400"
        },
        3: {
            iconColor: "text-cyan-500 dark:text-cyan-400",
            textColor: "text-gray-700 dark:text-gray-200",
            selectedTextColor: "text-cyan-700 dark:text-cyan-300",
            selectedBg: "bg-cyan-50/40 dark:bg-cyan-900/20",
            borderColor: "border-l-cyan-500 dark:border-l-cyan-400"
        }
    };

    const styles = levelStyles[level as keyof typeof levelStyles] || levelStyles[1];

    return (
        <div
            className={cn(
                "group relative cursor-pointer transition-all duration-200",
                "px-3 py-1.5",
                // Remove card appearance - just subtle hover and selection states
                isSelected
                    ? cn(styles.selectedBg, "border-l-2", styles.borderColor)
                    : "hover:bg-gray-50/50 dark:hover:bg-gray-800/50 border-l-2 border-transparent",
                // Minimal spacing
                "mb-px",
                // Focus state
                isFocused && "ring-1 ring-blue-400 dark:ring-blue-500 ring-offset-1 dark:ring-offset-gray-900"
            )}
            onClick={onSelect}
            role="treeitem"
            aria-selected={isSelected}
        >
            <div className="flex items-center gap-2">
                {/* Stacked/layered icon */}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={cn(
                        "h-3 w-3 flex-shrink-0",
                        isSelected ? styles.iconColor : "text-gray-400 dark:text-gray-500"
                    )}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                    />
                </svg>

                {/* Aspect name */}
                <span className={cn(
                    "text-xs flex-1",
                    isSelected ? styles.selectedTextColor : styles.textColor
                )}>
                    {aspect.name}
                </span>

                {/* Info icon for description */}
                {aspect.description && (
                    <div className="relative">
                        <button
                            className={cn(
                                "p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200",
                                "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
                            )}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowDescription(!showDescription);
                            }}
                            onMouseEnter={() => setShowDescription(true)}
                            onMouseLeave={() => setShowDescription(false)}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </button>

                        {/* Description tooltip */}
                        {showDescription && (
                            <div className="absolute right-0 top-full mt-1 w-48 p-2 bg-gray-900 dark:bg-gray-800 text-white text-[10px] rounded-md shadow-lg z-50 animate-fadeIn">
                                <div className="absolute right-1.5 -top-1 w-2 h-2 bg-gray-900 dark:bg-gray-800 transform rotate-45" />
                                {aspect.description}
                            </div>
                        )}
                    </div>
                )}

                {/* Navigation indicator */}
                {(hasChildren || isSelected) && (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={cn(
                            "h-2.5 w-2.5 transition-all duration-200 flex-shrink-0",
                            isSelected
                                ? "text-gray-400 dark:text-gray-500 opacity-60"
                                : "text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100"
                        )}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                )}
            </div>
        </div>
    );
};

interface MindMapColumnProps {
    level: 1 | 2 | 3 | 4;
    aspects: AppAspect[];
    themes: AppTheme[];
    selectedAspectId?: ID;
    selectedThemeId?: ID;
    focusedAspectId?: ID;
    onSelectAspect: (aspectName: ID, level: number) => void;
    onSelectTheme: (themeName: ID, isSubTheme: boolean, parentThemeName?: ID) => void;
    expandedThemes: Set<ID>;
    onToggleThemes: (aspectName: ID) => void;
    isDisabled: boolean;
    allThemes: AppTheme[];
    relationships: AppRelationship[];
    allAspects: AppAspect[];
    className?: string;
    globalFeedbackIdToNumberMap: Map<string, number>;
    columnTitle: string;
    parentAspectName?: ID;
    orgSlug?: string;
}

const MindMapColumn: React.FC<MindMapColumnProps> = ({
    level,
    aspects,
    themes,
    selectedAspectId,
    selectedThemeId,
    focusedAspectId,
    onSelectAspect,
    onSelectTheme,
    expandedThemes,
    onToggleThemes,
    isDisabled,
    allThemes,
    relationships,
    allAspects,
    className,
    globalFeedbackIdToNumberMap,
    columnTitle,
    parentAspectName,
    orgSlug,
}) => {
    const hasAspects = aspects && aspects.length > 0 && !aspects.some(a => a.name.startsWith('empty-') || a.name.includes('No '));
    const hasThemes = themes && themes.length > 0;

    // State for collapsing theme sections - collapsed by default unless no aspects
    const [isThemeSectionCollapsed, setIsThemeSectionCollapsed] = useState(!hasAspects && hasThemes ? false : true);
    // State for collapsing aspect sections - expanded by default
    const [isAspectSectionCollapsed, setIsAspectSectionCollapsed] = useState(false);

    // Auto-expand themes when there are no aspects but there are themes
    useEffect(() => {
        if (!hasAspects && hasThemes) {
            setIsThemeSectionCollapsed(false);
        }
    }, [hasAspects, hasThemes]);

    if (isDisabled) {
        return (
            <div className={cn(
                "flex flex-col h-full w-full",
                className
            )}>
                <div className="h-full flex flex-col items-center justify-center text-center">
                    <div className="text-gray-200 dark:text-gray-700 mb-2">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-10 w-10 mx-auto"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1}
                                d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                            />
                        </svg>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-600">
                        Select an item to explore
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn(
                "flex flex-col h-full w-full",
                className
            )}
        >
            <div className="flex-grow overflow-y-auto">
                {!hasAspects && !hasThemes ? (
                    <div className="p-6 text-center text-gray-400 dark:text-gray-600">
                        <p className="text-xs">No items available</p>
                    </div>
                ) : (
                    <div>
                        {/* Aspects section with collapsible header */}
                        {hasAspects && (
                            <div className="mb-3">
                                {/* Collapsible section header */}
                                <button
                                    onClick={() => setIsAspectSectionCollapsed(!isAspectSectionCollapsed)}
                                    className="w-full px-2 py-1.5 mb-1.5 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md transition-colors"
                                >
                                    <div className="flex items-center gap-1.5">
                                        {/* Stacked layers icon for structure */}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-3 w-3 text-gray-500 dark:text-gray-400"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={1.5}
                                                d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                                            />
                                        </svg>
                                        <h4 className="text-[10px] font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                                            L{level} Aspects ({aspects.length})
                                        </h4>
                                    </div>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={cn(
                                            "h-3 w-3 text-gray-400 dark:text-gray-500 transition-transform duration-200",
                                            isAspectSectionCollapsed ? "" : "transform rotate-90"
                                        )}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                        />
                                    </svg>
                                </button>

                                {/* Aspect items */}
                                {!isAspectSectionCollapsed && (
                                    <div className="animate-fadeIn">
                                        {aspects.map((aspect) => {
                                            const numericLevel = parseInt(aspect.level.replace('L', ''));
                                            const hasChildren = relationships.some(rel =>
                                                rel.parent_id === aspect.name && rel.type === 'aspect_to_aspect'
                                            );

                                            return (
                                                <FinderAspectCard
                                                    key={aspect.name}
                                                    aspect={aspect}
                                                    isSelected={aspect.name === selectedAspectId}
                                                    isFocused={aspect.name === focusedAspectId}
                                                    onSelect={() => onSelectAspect(aspect.name, numericLevel)}
                                                    hasChildren={hasChildren}
                                                    level={numericLevel}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Subtle divider between aspects and themes */}
                        {hasThemes && hasAspects && (
                            <div className="mx-3 mb-3 border-t border-gray-100 dark:border-gray-800" />
                        )}

                        {/* Themes section with collapsible header */}
                        {hasThemes && (
                            <div
                                className={cn(
                                    "relative",
                                    // Add a subtle glow/reflection effect to the entire theme section
                                    "before:absolute before:inset-0 before:bg-gradient-to-r before:from-purple-100/20 dark:before:from-purple-900/10 before:to-blue-100/20 dark:before:to-blue-900/10 before:blur-xl before:opacity-30 before:-z-10"
                                )}
                            >
                                {/* Collapsible section header */}
                                <button
                                    onClick={() => setIsThemeSectionCollapsed(!isThemeSectionCollapsed)}
                                    className="w-full px-2 py-1.5 mb-1.5 flex items-center justify-between text-left hover:bg-purple-50/30 dark:hover:bg-purple-900/20 rounded-md transition-colors"
                                >
                                    <div className="flex items-center gap-1.5">
                                        {/* Reflection/feedback icon for themes */}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-3 w-3 text-purple-500 dark:text-purple-400"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={1.5}
                                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                            />
                                        </svg>
                                        <h4 className="text-[10px] font-medium text-purple-600 dark:text-purple-400 uppercase tracking-wider italic">
                                            Feedback Themes ({themes.length})
                                        </h4>
                                    </div>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={cn(
                                            "h-3 w-3 text-purple-400 dark:text-purple-500 transition-transform duration-200",
                                            isThemeSectionCollapsed ? "" : "transform rotate-90"
                                        )}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                        />
                                    </svg>
                                </button>

                                {/* Theme items with glass-morphism container */}
                                {!isThemeSectionCollapsed && (
                                    <div className="animate-fadeIn">
                                        <div className={cn(
                                            "rounded-lg",
                                            "bg-gradient-to-br from-white/60 dark:from-gray-800/60 to-white/40 dark:to-gray-900/40",
                                            "backdrop-blur-md",
                                            "border border-white/20 dark:border-gray-700/20",
                                            "shadow-sm",
                                            "p-1"
                                        )}>
                                            {themes.map((theme) => (
                                                <ThemeCard
                                                    key={theme.name}
                                                    theme={theme}
                                                    aspectLevel={level - 1}
                                                    isSelected={theme.name === selectedThemeId}
                                                    onSelect={(themeName) => onSelectTheme(themeName, false)}
                                                    globalFeedbackIdToNumberMap={globalFeedbackIdToNumberMap}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MindMapColumn; 